/**
 * Local intent routing for "Lumio, write me an article about X".
 *
 * The real assistant will classify intents server-side and hand the studio a
 * structured action; until it does, the panel recognises this one request here
 * so the flow behind it (generating row in the blog list, draft created) can be
 * built and used. Deliberately small and conservative: anything it does not
 * recognise falls through to an ordinary question, which is the safe direction
 * to be wrong in.
 *
 * Three languages, because the owners who use this write in all of them: the
 * studio's own two (English, Spanish) plus Romanian.
 *
 * WHEN THE REAL INTENT ROUTING LANDS: delete this file.
 */

/** The thing being asked for: an article / post / entry. */
const SUBJECT = /\b(articles?|posts?|blog|articol(?:ul|e)?|art[íi]culos?|entradas?|publicaci[óo]n)\b/i;

/** The asking: write / create / want a new one. */
const INTENT =
  /\b(write|writing|draft|create|make|generate|compose|need|want|add|new|scrie|scrii|scriem|creeaz[ăa]|cre[ăa]m|f[ăa]|vreau|dore(?:sc|ști|sti)|genereaz[ăa]|nou[ăa]?|escrib[eir]+|redact[aer]+|crea[r]?|gener[aer]+|necesito|quiero|nuev[oa])\b/i;

/**
 * The brief hand-off: "about …" / "despre …" / "sobre …".
 *
 * Everything after it is captured WHOLE and sent to the service as the brief,
 * not reduced to a subject: "…about volatility, and cover bankroll size for a
 * short session" is a far better instruction than the word "volatility", and
 * the writer is given the whole sentence to work from.
 */
const TOPIC = /\b(?:about|on the topic of|regarding|covering|despre|referitor la|cu privire la|sobre|acerca de)\b\s+(.+)$/i;

/**
 * A question ABOUT article writing ("how do I write a post about bonuses?") is
 * not a request to write one. Leading interrogatives are the cheap, reliable
 * signal for that, so they opt the prompt out entirely.
 */
const QUESTION = /^(how|what|why|when|where|which|who|should|can|could|do|does|is|are|cum|ce|de ce|c[âa]nd|unde|care|cine|pot|trebuie|c[óo]mo|qu[ée]|por qu[ée]|cu[áa]ndo|d[óo]nde|cu[áa]l|qui[ée]n|puedo|debo)\b/i;

/** The shortest topic worth writing about; below it, treat the prompt as chat. */
const MIN_TOPIC = 3;

/**
 * The BRIEF of an article request, or `null` when the prompt is not one.
 *
 * A match needs all three parts: something asking (`INTENT`), the word for an
 * article (`SUBJECT`), and an explicit hand-off (`TOPIC`). Requiring the
 * hand-off is what keeps this honest: without it there is nothing to write
 * about, so the prompt is a conversation, not a job.
 *
 * What comes back is passed to the blog service verbatim as the brief. Nothing
 * here shortens it to a subject, and nothing downstream does either.
 */
export const detectArticleRequest = (prompt: string): string | null => {
  const text = prompt.trim();
  if (!text || QUESTION.test(text)) return null;
  if (!SUBJECT.test(text) || !INTENT.test(text)) return null;

  const topic = TOPIC.exec(text)?.[1]?.replace(/[\s.!?,;:]+$/, '').trim() ?? '';
  return topic.length >= MIN_TOPIC ? topic : null;
};
